import { useState, useEffect } from 'react';
import { axios } from '../App';
import { toast } from 'sonner';
import { Plus, Satellite, Plane, Waves, Droplets } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';

export default function Sensors() {
  const [sensors, setSensors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    sensor_type: 'radar',
    domain: 'air',
    status: 'operational',
    lat: '',
    lng: '',
    range_km: ''
  });

  useEffect(() => {
    loadSensors();
  }, []);

  const loadSensors = async () => {
    try {
      const { data } = await axios.get('/sensors');
      setSensors(data);
    } catch (error) {
      toast.error('Failed to load sensors');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        lat: parseFloat(formData.lat),
        lng: parseFloat(formData.lng),
        range_km: formData.range_km ? parseFloat(formData.range_km) : null
      };
      await axios.post('/sensors', payload);
      toast.success('Sensor deployed successfully');
      setOpen(false);
      setFormData({
        name: '',
        sensor_type: 'radar',
        domain: 'air',
        status: 'operational',
        lat: '',
        lng: '',
        range_km: ''
      });
      loadSensors();
    } catch (error) {
      toast.error('Failed to deploy sensor');
    }
  };

  const domainIcons = {
    air: Plane,
    surface: Waves,
    underwater: Droplets
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-sky-500 font-mono text-sm">LOADING SENSOR NETWORK...</div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="sensors-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold tracking-tight uppercase">Sensor Network</h1>
          <p className="text-xs text-zinc-500 uppercase tracking-wider mt-1 font-mono">
            Multi-domain detection grid
          </p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-sky-600 hover:bg-sky-500 text-white rounded-none uppercase text-xs border border-sky-400/20" data-testid="deploy-sensor-button">
              <Plus size={16} className="mr-2" />
              Deploy Sensor
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold uppercase">Deploy New Sensor</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="sensor-form">
              <div>
                <Label className="text-xs font-mono text-zinc-500 uppercase">Sensor Name</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="mt-2 bg-zinc-900/50 border-zinc-800 rounded-none"
                  required
                  placeholder="RADAR-001"
                  data-testid="sensor-name-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-mono text-zinc-500 uppercase">Type</Label>
                  <Select
                    value={formData.sensor_type}
                    onValueChange={(value) => setFormData({ ...formData, sensor_type: value })}
                  >
                    <SelectTrigger className="mt-2 bg-zinc-900/50 border-zinc-800 rounded-none" data-testid="sensor-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800">
                      <SelectItem value="radar">Radar</SelectItem>
                      <SelectItem value="acoustic">Acoustic</SelectItem>
                      <SelectItem value="rf">RF Detector</SelectItem>
                      <SelectItem value="optical">Optical</SelectItem>
                      <SelectItem value="thermal">Thermal</SelectItem>
                      <SelectItem value="sonar">Sonar</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-mono text-zinc-500 uppercase">Domain</Label>
                  <Select
                    value={formData.domain}
                    onValueChange={(value) => setFormData({ ...formData, domain: value })}
                  >
                    <SelectTrigger className="mt-2 bg-zinc-900/50 border-zinc-800 rounded-none" data-testid="sensor-domain-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800">
                      <SelectItem value="air">Air</SelectItem>
                      <SelectItem value="surface">Surface</SelectItem>
                      <SelectItem value="underwater">Underwater</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="text-xs font-mono text-zinc-500 uppercase">Latitude</Label>
                  <Input
                    type="number"
                    step="any"
                    value={formData.lat}
                    onChange={(e) => setFormData({ ...formData, lat: e.target.value })}
                    className="mt-2 bg-zinc-900/50 border-zinc-800 rounded-none"
                    required
                    placeholder="38.9072"
                    data-testid="sensor-lat-input"
                  />
                </div>
                <div>
                  <Label className="text-xs font-mono text-zinc-500 uppercase">Longitude</Label>
                  <Input
                    type="number"
                    step="any"
                    value={formData.lng}
                    onChange={(e) => setFormData({ ...formData, lng: e.target.value })}
                    className="mt-2 bg-zinc-900/50 border-zinc-800 rounded-none"
                    required
                    placeholder="-77.0369"
                    data-testid="sensor-lng-input"
                  />
                </div>
                <div>
                  <Label className="text-xs font-mono text-zinc-500 uppercase">Range (km)</Label>
                  <Input
                    type="number"
                    step="any"
                    value={formData.range_km}
                    onChange={(e) => setFormData({ ...formData, range_km: e.target.value })}
                    className="mt-2 bg-zinc-900/50 border-zinc-800 rounded-none"
                    placeholder="10"
                    data-testid="sensor-range-input"
                  />
                </div>
              </div>
              <Button
                type="submit"
                className="w-full bg-sky-600 hover:bg-sky-500 text-white rounded-none uppercase text-xs border border-sky-400/20"
                data-testid="submit-sensor-button"
              >
                Deploy Sensor
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {sensors.map((sensor) => {
          const DomainIcon = domainIcons[sensor.domain] || Satellite;
          return (
            <div
              key={sensor.id}
              className="bg-zinc-950/80 border border-zinc-800 rounded-sm p-6 hover:border-zinc-700 transition-colors corner-accent"
              data-testid={`sensor-card-${sensor.id}`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <Satellite className="text-sky-500" size={24} />
                  <DomainIcon className="text-emerald-500" size={20} />
                </div>
                <span
                  className={`text-xs font-mono uppercase px-2 py-1 rounded-sm ${
                    sensor.status === 'operational'
                      ? 'bg-emerald-900/50 text-emerald-300 border border-emerald-800'
                      : 'bg-zinc-800 text-zinc-400 border border-zinc-700'
                  }`}
                >
                  {sensor.status}
                </span>
              </div>
              <h3 className="text-lg font-bold uppercase tracking-tight mb-2">{sensor.name}</h3>
              <div className="space-y-1 text-sm text-zinc-400">
                <div className="flex items-center justify-between">
                  <span className="text-zinc-600 font-mono text-xs uppercase">Type:</span>
                  <span>{sensor.sensor_type}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-zinc-600 font-mono text-xs uppercase">Domain:</span>
                  <span>{sensor.domain}</span>
                </div>
                {sensor.range_km && (
                  <div className="flex items-center justify-between">
                    <span className="text-zinc-600 font-mono text-xs uppercase">Range:</span>
                    <span>{sensor.range_km} km</span>
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2 mt-4">
                <div
                  className={`w-2 h-2 rounded-full ${
                    sensor.status === 'operational'
                      ? 'bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.8)]'
                      : 'bg-zinc-600'
                  }`}
                ></div>
                <span className="text-xs font-mono text-zinc-600 uppercase">Active</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}